package gr.tech.pdfgenerator.styles

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.layer.SilkLayer
import com.varabyte.kobweb.silk.style.layer.add
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    ctx.stylesheet.cssLayers.add("bulmacss", after = SilkLayer.BASE)

    ctx.stylesheet.registerStyleBase("html") {
        Modifier
            .scrollBehavior(ScrollBehavior.Smooth)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }
}

/*
    General Text Styles
*/
val SimpleSpanStyle = CssStyle {
    base {
        Modifier
            .fontSize(1.cssRem)
            .fontWeight(FontWeight.Light)
            .fontFamily(FONT_FAMILY)
    }
}

fun Modifier.noBorder(): Modifier {
    return this
        .border (
            width = 0.px,
            style = LineStyle.None,
            color = Colors.Transparent
        )
        .outline(
            width = 0.px,
            style = LineStyle.None,
            color = Colors.Transparent
        )
}

val ButtonStyle = CssStyle {
    base {
        Modifier
            .margin(top = 16.px)
            .padding(leftRight = 12.px, topBottom = 6.px)
            .backgroundColor(Theme.MediumGreen.rgb)
            .noBorder()
            .borderRadius(4.px)
            .color(Colors.White)
            .fontSize(1.cssRem)
            .cursor(Cursor.Pointer)
    }
    hover {
        Modifier
            .transition(Transition.all(100.ms))
            .backgroundColor(Theme.DarkGreen.rgb)
    }
}

val TableHeaderStyle = CssStyle {
    base {
        Modifier
            .textAlign(TextAlign.Center)
    }
}

val TableRowHoverStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .outline(
                width = 2.px,
                style = LineStyle.Solid,
                color = Colors.Transparent
            )
            .transition(Transition.all(250.ms))
    }
    hover {
        Modifier
            .outline(
                width = 2.px,
                style = LineStyle.Solid,
                color = Theme.MediumGreen.rgb
            )
    }
    cssRule(":hover div") {
        Modifier
            .transition(Transition.all(250.ms))
            .opacity(100)
    }
}

val TableRowButtonStyle = CssStyle {
    base {
        Modifier
            .opacity(0)
            .position(Position.Absolute).right(1.cssRem).top(0.px)
            .height(100.percent).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column)
            .justifyContent(JustifyContent.Center)
    }
}

val TableDataStyle = Modifier
    .minHeight(40.px)
    .padding(5.px)
    .textAlign(TextAlign.Start)
    .fontSize(14.px)
    .overflow(Overflow.Hidden)
    .whiteSpace(WhiteSpace.NoWrap)
    .textOverflow(TextOverflow.Ellipsis)

val SmallColumnStyle = TableDataStyle
    .width(50.px) // Checkbox, ID

val MediumColumnStyle = TableDataStyle
    .minWidth(200.px)
    .maxWidth(200.px)

val LargeColumnStyle = TableDataStyle
    .minWidth(400.px)
    .maxWidth(400.px)
    .whiteSpace(WhiteSpace.Normal)

val ShowMoreButtonStyle = CssStyle {
    base {
        Modifier
            .margin(topBottom = 10.px)
            .textAlign(TextAlign.Center)
            .border(
                width = 1.px, LineStyle.Solid, Colors.Black
            )
            .padding(
                topBottom = 10.px,
                leftRight = 15.px
            )
            .fontFamily(FONT_FAMILY)
            .fontSize(16.px)
            .fontWeight(FontWeight.Medium)
            .cursor(Cursor.Pointer)
    }
}

val GeneralButtonStyle = CssStyle {
    base {
        Modifier
            .boxShadow(0.px, 5.px, 10.px, -5.px, rgba(100, 100, 111, 0.8))
            .backgroundColor(Colors.White)
    }
    hover {
        Modifier
            .transition(Transition.all(300.ms))
            .backgroundColor(Colors.LightGray)
            .scale(0.99)
    }
}

val MediumGreenHoverLink = CssStyle {
    base {
        Modifier
            .color(Colors.Black)
    }
    hover {
        Modifier
            .textDecorationLine(TextDecorationLine.Underline)
    }
}